import React, { useCallback, useEffect, useState } from 'react';
import Dialog from "../../components/controls/Dialog";
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import * as clientService from '../../services/clientService';
import * as skillService from '../../services/skillService';
import useAlert from '../../components/useAlert';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    IconButton, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Box,
    Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const ClientSkillsDialog = (props) => {
    const { open, setOpen, selectedRow, setSelectedRow } = props;
    const [clientSkills, setClientSkills] = useState([]);
    const [allSkills, setAllSkills] = useState([]);
    const [selectedSkillId, setSelectedSkillId] = useState('');

    const {
        Alert,
        status,
        showSuccess,
        showError,
        handleAlertClose
    } = useAlert();

    const fetchAllSkills = useCallback(() => {
        skillService.getSkills()
            .then(response => {
                setAllSkills(response.data.items || []);
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error fetching skills");
            });
    }, [showError]);

    useEffect(() => {
        if (open && selectedRow) {
            handleAlertClose();
            setClientSkills(selectedRow.skills || []);
            fetchAllSkills();
        }
    }, [open, selectedRow, fetchAllSkills, handleAlertClose]);

    const handleClose = () => {
        setOpen(false);
        setSelectedRow(null);
    };

    const handleBindSkill = () => {
        if (!selectedSkillId) return;
        clientService.bindClientSkill(selectedRow.id, selectedSkillId, clientSkills)
            .then(() => {
                showSuccess("Skill bound successfully");
                if (props.getProjectClients) {
                    props.getProjectClients();
                } else {
                    const newSkill = allSkills.find(skill => skill.id === selectedSkillId);
                    if (newSkill) {
                        setClientSkills((prev) => [...prev, newSkill]);
                    }
                }
                setSelectedSkillId('');
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error binding skill");
            });
    };

    const handleUnbindSkill = (skillId) => {
        clientService.unbindClientSkill(selectedRow.id, skillId, clientSkills)
            .then(() => {
                showSuccess("Skill unbound successfully");
                if (props.getProjectClients) {
                    props.getProjectClients();
                } else {
                    setClientSkills((prev) => prev.filter(skill => skill.id !== skillId));
                }
            })
            .catch(error => {
                showError(error.response?.data?.message || "Error unbinding skill");
            });
    };

    return (
        <React.Fragment>
            {status ? <Alert /> : null}
            <Dialog
                title={`Manage Skills for Client: ${selectedRow?.name}`}
                open={open}
                handleClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-end' }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Add Skill</InputLabel>
                            <Select
                                value={selectedSkillId}
                                label="Add Skill"
                                onChange={(e) => setSelectedSkillId(e.target.value)}
                            >
                                {allSkills
                                    .filter(s => !clientSkills.find(cs => cs.id === s.id))
                                    .map(s => (
                                        <MenuItem key={s.id} value={s.id}>
                                            {s.name} ({s.id}) - {s.projectId}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                        <Button 
                            variant="contained" 
                            startIcon={<AddIcon />}
                            onClick={handleBindSkill}
                            disabled={!selectedSkillId}
                        >
                            Add
                        </Button>
                    </Box>

                    <Typography variant="h6" gutterBottom>
                        Assigned Skills
                    </Typography>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Skill ID</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientSkills.map((skill) => (
                                    <TableRow key={skill.id}>
                                        <TableCell>{skill.id}</TableCell>
                                        <TableCell>{skill.name}</TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                size="small" 
                                                color="error"
                                                onClick={() => handleUnbindSkill(skill.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {clientSkills.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No skills assigned to this client.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default ClientSkillsDialog;
